'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { login } from '@/app/apis/users';
import { useAuth } from '@/app/context/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const router = useRouter();
  const auth = useAuth();
  const loginMode: 'pwd' | 'otc' = 'pwd';
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
 
  if (!isOpen || !mounted) return null;

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    // 处理忘记密码逻辑
    console.log('忘记密码');
  };
               
  const handleSendCode = (e: React.MouseEvent) => {
    e.preventDefault();
    // 处理发送验证码逻辑
    console.log('发送验证码');
  };

  const handleLoginByOtc = (e: React.MouseEvent) => {
    e.preventDefault();
    // 处理验证码登录逻辑
    console.log('验证码登录');
  };

  const handleCreateAccount = (e: React.MouseEvent) => {
    e.preventDefault();
    // 关闭登录弹出窗
    onClose();
    // 跳转到注册页面
    router.push('/access/register');
  };

  // 备用：如未来需要在弹窗内切换登录方式，可启用以下方法并在 UI 上添加入口
  // const handleSwitchToOtc = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   setLoginMode('otc');
  // };
  // const handleSwitchToPwd = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   setLoginMode('pwd');
  // };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!email || !password) {
      setErrorMsg('Email and password are required.');
      return;
    }

    try {
      setSubmitting(true);
      const resp = await login({ email, password });
      if (resp.code === 0) {
        const user = resp.data;
        if (typeof window !== 'undefined' && user?.token) {
          window.localStorage.setItem('token', user.token);
          window.localStorage.setItem('user_email', user.email);
          window.localStorage.setItem('user_id', String(user.id));
        }
        // 通知全局登录成功
        if (user?.token) {
          auth.loginSuccess({ id: user.id, email: user.email, token: user.token });
        }
        onClose();
        router.push('/');
      } else {
        setErrorMsg(resp.message || 'Login failed.');
      }
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }, message?: string };
      const msg =
        anyErr?.response?.data?.message ||
        anyErr?.message ||
        'Login failed, please try again.';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div 
      id="login-modal" 
      className="login-modal" 
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal-content">
        <span className="close" onClick={onClose}>×</span>
        <h2>Sign In</h2>
        <p className="login-subtitle">Enter your credentials to access your account</p>
        <div className="alert-info" id="login-alert" style={{ display: errorMsg ? 'block' : 'none' }}>
          {errorMsg || 'Email or password is incorrect.'}
        </div>
        <form id="login-form" onSubmit={handleFormSubmit} style={{ display: loginMode === 'pwd' ? 'block' : 'none' }}>
          <label htmlFor="loginkey_pwd">Email</label>
          <input
            type="email"
            autoComplete="username"
            id="loginkey_pwd"
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label htmlFor="login_password">Password</label>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            id="login_password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="options">
            <label className="stay-signed-in">
              <input type="checkbox" id="stay-signed-in" />
              Stay Signed In
            </label>
            <a href="#" onClick={handleForgotPassword} className="forgot-password">Forgot password?</a>
          </div>
          <button className="login-btn" type="submit" disabled={submitting}>
            {submitting ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        <div id="login-otc" style={{ display: 'none' }}>
          <label htmlFor="loginkey_otc">Phone Number</label>
          <input type="text" autoComplete="username" id="loginkey_otc" required />
          <div className="input-group">
            <label htmlFor="vercode-otc">Verification Code</label>
            <div className="code-container">
              <input type="text" id="vercode-otc" autoComplete="off" />
              <button className="send-code-btn" id="send-code-otc" type="button" onClick={handleSendCode}>Send</button>
            </div>
            <div className="item-alert" id="code-alert-otc">Verification code has been sent to your phone. Valid for 15 minutes.</div>
          </div>
          <button className="login-btn" id="submit-otc" type="button" onClick={handleLoginByOtc}>Sign In</button>
        </div>
        <p className="new-customer">Don&apos;t have an account? <a href="#" onClick={handleCreateAccount}>Sign up</a></p>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
